import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Alert } from '@/components/ui/Alert';
import { Spinner } from '@/components/ui/Spinner';
import { formatRelative } from '@/lib/format';
import { useAlerts, useLeadsWithoutNextAction, type AlertRow } from './useTasks';

const SEVERITY_TONE: Record<number, string> = {
  3: 'border-red-200 bg-red-50',
  2: 'border-amber-200 bg-amber-50',
  1: 'border-slate-200 bg-white',
};

function AlertGroup({ kind, rows }: { kind: AlertRow['alert']; rows: AlertRow[] }) {
  const { t } = useTranslation();

  return (
    <section className="flex flex-col gap-2">
      <h2 className="text-sm font-semibold text-slate-800">
        {t(`alerts.${kind}` as 'alerts.uncontacted')}{' '}
        <span className="font-normal text-slate-500">({rows.length})</span>
      </h2>
      <p className="text-xs text-slate-500">
        {t(`alerts.${kind}_why` as 'alerts.uncontacted_why')}
      </p>

      <ul className="flex flex-col gap-1.5">
        {rows.slice(0, 50).map((row) => (
          <li
            key={`${row.alert}-${row.lead_id}`}
            className={`flex flex-wrap items-center justify-between gap-2 rounded-lg border p-2.5 ${
              SEVERITY_TONE[row.severity] ?? SEVERITY_TONE[1]
            }`}
          >
            <Link
              to={`/leads/${row.lead_id}`}
              className="font-medium text-brand-800 hover:underline"
            >
              {row.full_name}
            </Link>
            <span className="text-xs text-slate-600">{formatRelative(row.since)}</span>
          </li>
        ))}
      </ul>

      {rows.length > 50 ? (
        <p className="text-xs text-slate-400">{t('alerts.more', { count: rows.length - 50 })}</p>
      ) : null}
    </section>
  );
}

export default function AlertsPage() {
  const { t } = useTranslation();
  const { data, isPending, isError } = useAlerts();
  const { data: noNext } = useLeadsWithoutNextAction();

  if (isPending) return <Spinner />;
  if (isError) return <Alert tone="error">{t('common.error')}</Alert>;

  const rows = data ?? [];
  const kinds: AlertRow['alert'][] = [
    'uncontacted',
    'sla_breached',
    'overdue_task',
    'hot_no_activity',
    'stalled',
  ];
  const grouped = kinds
    .map((kind) => ({ kind, rows: rows.filter((r) => r.alert === kind) }))
    .filter((g) => g.rows.length > 0);

  return (
    <section className="flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-bold text-slate-900">{t('nav.alerts')}</h1>
        <p className="mt-1 text-sm text-slate-500">{t('alerts.intro')}</p>
      </div>

      {grouped.length === 0 && (noNext?.length ?? 0) === 0 ? (
        <Alert tone="success">{t('alerts.allClear')}</Alert>
      ) : null}

      {grouped.map((g) => (
        <AlertGroup key={g.kind} kind={g.kind} rows={g.rows} />
      ))}

      {/* #11 and #42: not strictly an alert, but the single most useful list a
          manager can look at — leads nobody has committed to contacting again. */}
      {noNext && noNext.length > 0 ? (
        <section className="flex flex-col gap-2">
          <h2 className="text-sm font-semibold text-slate-800">
            {t('alerts.noNextAction')}{' '}
            <span className="font-normal text-slate-500">({noNext.length})</span>
          </h2>
          <p className="text-xs text-slate-500">{t('alerts.noNextAction_why')}</p>
          <ul className="flex flex-col gap-1.5">
            {noNext.slice(0, 50).map((row) => (
              <li
                key={row.lead_id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white p-2.5"
              >
                <Link
                  to={`/leads/${row.lead_id}`}
                  className="font-medium text-brand-800 hover:underline"
                >
                  {row.full_name}
                </Link>
                <span className="text-xs text-slate-600">
                  {t(`stages.${row.stage}` as 'stages.new')}
                </span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}
    </section>
  );
}
